import { useEffect, useState } from 'react';
import { api, uploadUrl } from '../api/client.js';

const DEFAULT_ICONS = ['menu_book', 'auto_awesome', 'emoji_events', 'mic'];
const COLORS = [
  { bg: 'color-mix(in oklab, var(--accent) 10%, transparent)', icon: 'var(--accent)' },
  { bg: 'rgba(255,199,44,0.1)', icon: '#E6A000' },
  { bg: 'rgba(220,80,60,0.1)', icon: '#DC503C' },
  { bg: 'color-mix(in oklab, var(--accent) 10%, transparent)', icon: 'var(--color-forest-400)' },
];

const STATS = [
  { num: '200+', label: 'Event Sukses', icon: 'calendar_month' },
  { num: '50+', label: 'Spesies Satwa', icon: 'pets' },
  { num: '10K+', label: 'Peserta Bahagia', icon: 'mood' },
  { num: '5★', label: 'Rating Rata-rata', icon: 'star' },
];

export default function ServicesSection() {
  const [layanan, setLayanan] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [bounceId, setBounceId] = useState(null);

  useEffect(() => {
    api.get('/layanan').then(({ data }) => setLayanan(data.layanan || []));
  }, []);

  return (
    <section id="layanan" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold mb-4 border" style={{ background: 'color-mix(in oklab, var(--accent) 8%, transparent)', borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)', color: 'var(--accent)' }}>
            <span className="material-symbols-outlined">grid_view</span> LAYANAN KAMI
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4">
            Layanan Spesialis <span style={{ color: 'var(--accent)' }}>Satwa</span> Kami
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400 max-w-xl mx-auto leading-relaxed">
            Kami menghadirkan pengalaman edukatif dan interaktif bersama satwa untuk berbagai kebutuhan institusi Anda.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {layanan.map((l, i) => (
            <ServiceCard
              key={l.id}
              l={l}
              color={COLORS[i % COLORS.length]}
              icon={l.icon || DEFAULT_ICONS[i % DEFAULT_ICONS.length]}
              isOpen={openId === l.id}
              bounce={bounceId === l.id}
              onToggle={() => { setOpenId(openId === l.id ? null : l.id); setBounceId(l.id); }}
              onBounceEnd={() => setBounceId((b) => (b === l.id ? null : b))}
            />
          ))}
        </div>

        <div className="mt-16 rounded-3xl p-8 grid grid-cols-2 md:grid-cols-4 gap-6" style={{ background: 'linear-gradient(135deg, var(--accent), var(--accent-hover))' }}>
          {STATS.map((s) => (
            <div key={s.label} className="text-center text-white">
              <span className="material-symbols-outlined text-4xl text-yellow-400 mb-3 block">{s.icon}</span>
              <div className="font-display text-3xl font-bold">{s.num}</div>
              <div className="text-sm text-white/70 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ l, color, icon, isOpen, bounce, onToggle, onBounceEnd }) {
  const [openSubIds, setOpenSubIds] = useState(new Set());
  const steps = l.durasi_proses ? l.durasi_proses.split('→').map((s) => s.trim()) : [];
  const subs = l.sub_layanan || [];

  const toggleSub = (id) => {
    setOpenSubIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div
      onClick={onToggle}
      onAnimationEnd={onBounceEnd}
      className={`group relative rounded-3xl transition-all duration-300 hover:shadow-xl cursor-pointer bg-white dark:bg-neutral-900 border border-black/[0.08] dark:border-white/[0.08] ${isOpen ? 'shadow-2xl' : ''} ${bounce ? 'card-click-bounce' : ''}`}
    >
      <div className="p-8">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110" style={{ background: color.bg }}>
          <span className="material-symbols-outlined text-2xl" style={{ color: color.icon }}>{icon}</span>
        </div>
        <h3 className="font-display font-bold text-xl text-neutral-900 dark:text-white mb-3">{l.nama}</h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{l.deskripsi}</p>
        <button className="mt-6 flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--accent)' }}>
          <span>{isOpen ? 'Tutup' : 'Pelajari lebih'}</span>
          <span className="material-symbols-outlined transition-transform duration-300" style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }}>expand_more</span>
        </button>
      </div>

      <div className="grid transition-[grid-template-rows] duration-500 ease-out" style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}>
        <div className="overflow-hidden">
          {/* Jaring pengaman: konten expand tidak akan pernah mendorong layout tanpa batas,
              kalau kontennya panjang (banyak sub-layanan dibuka sekaligus, dll) akan scroll di dalam. */}
          <div className="px-8 pb-8 space-y-5 border-t pt-5 overflow-y-auto" style={{ borderColor: color.bg, maxHeight: '65vh' }}>
            {l.benefit?.length > 0 && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest mb-3 text-neutral-400">APA YANG ANDA DAPAT</p>
                <ul className="space-y-2">
                  {l.benefit.map((b, bi) => (
                    <li key={bi} className="flex items-start gap-2.5">
                      <span className="material-symbols-outlined text-base mt-0.5 flex-shrink-0" style={{ color: color.icon, fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                      <span className="text-sm text-neutral-700 dark:text-neutral-300 leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {l.target_audience && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest mb-2 text-neutral-400">COCOK UNTUK</p>
                <div className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <span className="material-symbols-outlined text-base" style={{ color: color.icon }}>group</span>
                  {l.target_audience}
                </div>
              </div>
            )}

            {subs.length > 0 && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest mb-3 text-neutral-400">LAYANAN YANG TERSEDIA</p>
                <div className="space-y-1.5">
                  {subs.map((s) => {
                    const subOpen = openSubIds.has(s.id);
                    return (
                      <div key={s.id} className="rounded-xl overflow-hidden border border-black/5 dark:border-white/5">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleSub(s.id); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors"
                        >
                          <span className="material-symbols-outlined text-sm flex-shrink-0" style={{ color: color.icon }}>subdirectory_arrow_right</span>
                          <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-200 flex-1">{s.nama}</span>
                          {s.deskripsi && (
                            <span className="material-symbols-outlined text-sm text-neutral-300 transition-transform duration-300 flex-shrink-0" style={{ transform: subOpen ? 'rotate(180deg)' : 'none' }}>
                              expand_more
                            </span>
                          )}
                        </button>
                        {subOpen && s.deskripsi && (
                          <p className="px-3 pb-2.5 pl-9 text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">{s.deskripsi}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {steps.length > 0 && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest mb-3 text-neutral-400">ALUR PROSES</p>
                <div className="flex flex-wrap items-center gap-1">
                  {steps.map((step, si) => (
                    <span key={si} className="flex items-center gap-1">
                      <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ background: color.bg, color: color.icon }}>{step}</span>
                      {si < steps.length - 1 && <span className="material-symbols-outlined text-xs text-neutral-300">chevron_right</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {l.image && (
              <div className="rounded-2xl overflow-hidden h-36 bg-neutral-100">
                <img src={uploadUrl(l.image)} alt={l.nama} className="w-full h-full object-cover" />
              </div>
            )}
            <a href="/kontak" onClick={(e) => e.stopPropagation()} className="flex items-center justify-center gap-2.5 w-full px-5 py-3 rounded-xl text-sm font-bold text-white shadow-md transition-all duration-300 hover:rounded-2xl active:rounded-3xl active:scale-[0.98]" style={{ background: `linear-gradient(135deg, ${color.icon}, color-mix(in oklab, ${color.icon} 80%, black))` }}>
              <span className="material-symbols-outlined text-lg flex-shrink-0">edit_calendar</span>
              <span>Hubungi & Dapatkan Penawaran</span>
            </a>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-8 right-8 h-0.5 rounded-full scale-x-0 group-hover:scale-x-100 transition-transform duration-300" style={{ background: `linear-gradient(90deg, ${color.icon}, #FFC72C)` }} />
    </div>
  );
}
